<template>
  <div>
    <lodge-info />
    <breadcrumb
      :paths="[
        {
          route: {
            name: isAdmin ? 'adminlodgebirds' : 'lodgebirds',
            params: { id: hotelId },
          },
          title: 'birds',
        },
        { title: 'select' },
      ]"
    />

    <div class="LodgeBirdsSelect_filters__container">
      <form @submit.prevent="handleNewSearch">
        <div class="dashboard__filters_container">
          <base-input
            id="searchInput"
            padding="p-2"
            icon="solid-search"
            placeholder="common_or_scientific_name"
            width="LodgeBirsSelect_filters__input"
            v-model="request.birds.search"
          />

          <div class="LodgeBirdsSelect_filters__multiselect_container">
            <multiselect
              label="name"
              track-by="id"
              open-direction="bottom"
              id="family_group_search"
              :allowEmpty="false"
              :placeholder="$t('family_group')"
              :internal-search="false"
              :limit="10"
              :hide-selected="true"
              :searchable="true"
              :clear-on-select="false"
              :close-on-select="true"
              :options="request.familyGroup.data"
              :loading="request.familyGroup.isLoading"
              :preserve-search="true"
              :show-labels="false"
              @search-change="getFamilyGroup"
              @open="loadIfEmpty(request.familyGroup, getFamilyGroup)"
              v-model="form.familyGroup"
              @input="clearFamily()"
            />

            <multiselect
              label="name"
              track-by="id"
              open-direction="bottom"
              id="family_search"
              :allowEmpty="false"
              :placeholder="$t('family')"
              :internal-search="false"
              :limit="10"
              :hide-selected="true"
              :searchable="true"
              :clear-on-select="false"
              :close-on-select="true"
              :options="request.family.data"
              :loading="request.family.isLoading"
              :preserve-search="true"
              :show-labels="false"
              @search-change="getFamily"
              @open="loadIfEmpty(request.family, getFamily)"
              v-model="form.family"
              @input="fixFamilyGroup()"
            />
          </div>
        </div>
        <div class="flex gap-4">
          <base-button type="submit" padding="p-0" class="w-24 h-11">
            {{ $t('search') }}
          </base-button>
          <base-button
            type="button"
            bg-color="bg-error"
            padding="p-0"
            class="w-24 h-11"
            @click="clearFilters(), handleNewSearch()"
          >
            {{ $t('clear') }}
          </base-button>
        </div>
      </form>
    </div>

    <base-table
      :is-loading="request.birds.loading || request.birdsSelected.loading"
      :data="request.birds.data"
      :columns="tableColumns"
      key="LodgeBirdsSelectTable"
    >
      <template v-slot:bird="{ content, isLoading, _class, tooltipDirection }">
        <base-tooltip
          :direction="tooltipDirection"
          y-align="right"
          no-wrap
          :openWithHover="!isLoading"
          class="cursor-text"
        >
          <div :class="_class">
            <div class="LodgeBirdsSelect_table__bird">
              <span class="hidden sm:block">
                <base-image :src="content.img || defaultImage" circle />
              </span>
              <div class="flex flex-col">
                <span class="font-base">
                  {{ content.specie.commonName }}
                </span>
                <span class="text-gray-default">
                  ({{ content.specie.scientific }})
                </span>
              </div>
            </div>
          </div>
          <template v-slot:tooltip>
            <div class="flex flex-col">
              <span class="font-base">
                {{ content.specie.commonName }}
              </span>
              <span class="text-gray-default">
                ({{ content.specie.scientific }})
              </span>
            </div>
          </template>
        </base-tooltip>
      </template>
      <template v-slot:actions="{ id, _class }">
        <div :class="[_class, 'LodgeBirdsSelect_table__actions']">
          <base-button
            padding="py-0 px-1"
            text-size="font-size"
            text-color="text-black"
            bg-color="bg-transparent"
            border="border-secondary border"
            icon="solid-close-time"
            icon-width="18"
            icon-class="text-error relative top-1"
            @click="handleDeleteBird(id)"
            key="unselect"
            v-if="request.birdsSelected.data.includes(id)"
          >
            <span> {{ $t('remove') }} </span>
          </base-button>
          <base-button
            padding="py-0 px-1"
            text-size="font-size"
            icon="solid-plus"
            icon-width="14"
            icon-class="relative top-px left-px"
            @click="editBird(id)"
            key="select"
            v-else
          >
            <span> {{ $t('add') }} </span>
          </base-button>
        </div>
      </template>
    </base-table>
    <table-pagination
      class="table-pagination"
      :min="1"
      :max="request.birds.pagination.maxPages"
      :actual="request.birds.pagination.actualPage"
      :is-loading="request.birds.loading"
      @changePage="getBirds"
    />

    <bird-edit
      :bird="birdToEdit(modal.edit.birdIndex)"
      v-if="modal.edit.isEditing"
      :show="modal.edit.isEditing"
      @clickOutside="closeModals"
    >
      <template v-slot:actions="{ bird, isValid, updateIsNotable }">
        <base-button
          @click="closeModals"
          theme="text-only"
          text-color="text-gray-default text-opacity-60"
        >
          {{ $t('cancel') }}
        </base-button>
        <base-button
          :is-loading="modal.edit.isLoading"
          :disable="!isValid"
          @click="saveEdit({ bird, updateIsNotable })"
        >
          {{ $t('save') }}
        </base-button>
      </template>
    </bird-edit>

    <bird-delete
      :show="modal.delete.isDeleting"
      :is-loading="modal.delete.isLoading"
      @cancel="modal.delete.isDeleting = false"
      @delete="deleteBird"
      @clickOutside="modal.delete.isDeleting = false"
    />
  </div>
</template>

<script>
import {
  BaseButton,
  BaseInput,
  BaseImage,
  BaseTable,
  BaseTooltip,
  BirdEdit,
  BirdDelete,
  Breadcrumb,
  LodgeInfo,
  TablePagination,
} from '@/components';

import Multiselect from 'vue-multiselect';

import TableLodgeBirdsSelect from './TableLodgeBirdsSelect.js';
import defaultImage from '@/assets/UploadImageDisable.png';

import {
  createWordToSearch,
  calculateMaxPages,
  createQuery,
  createQueryFromInput,
  parseInputQuery,
  parsePageQuery,
  parseQuery,
} from '@/utils/pagination.js';

import {
  GET_ALL_BIRDS_STCALA_WITH_FILTERS_QUERY,
  GET_ALL_FAMILY_GROUPS_QUERY,
  GET_ALL_FAMILIES_QUERY,
  INSERT_BIRDS_BY_HOTEL_MUTATION,
  GET_BIRDS_SELECTED_BY_HOTEL_QUERY,
  DELETE_BIRD_BY_HOTEL_MUTATION,
} from '@/services/GraphQl';

import searchTools from '@/mixins/searchTools';

export default {
  mixins: [searchTools],
  data() {
    return {
      modal: {
        edit: {
          isEditing: false,
          birdIndex: null,
          isLoading: false,
        },
        delete: {
          isDeleting: false,
          birdId: null,
          hotelId: null,
          isLoading: false,
        },
      },
      form: {
        familyGroup: null,
        family: null,
      },
      request: {
        familyGroup: {
          isLoading: false,
          search: null,
          data: [],
          limit: 5,
        },
        family: {
          isLoading: false,
          search: null,
          data: [],
          limit: 5,
        },
        birds: {
          pagination: {
            actualPage: 1,
            limit: 10,
            maxPages: 1,
          },
          search: null,
          loading: false,
          data: [{}],
        },
        birdsSelected: {
          loading: false,
          data: [],
        },
      },
    };
  },
  components: {
    BaseButton,
    BaseInput,
    BaseImage,
    BaseTable,
    BaseTooltip,
    BirdEdit,
    BirdDelete,
    Breadcrumb,
    LodgeInfo,
    Multiselect,
    TablePagination,
  },
  methods: {
    loadIfEmpty(requestObject, callback) {
      const { isLoading, search, data } = requestObject;

      if (!data.length && !isLoading && !search) {
        callback();
      }
    },
    fixFamilyGroup() {
      const { family_group } = this.form.family;

      this.$set(this.form, 'familyGroup', {
        name: family_group.name_en,
        id: family_group.id,
      });
    },
    clearFamily() {
      this.$set(this.form, 'family', null);
      this.getFamily(this.request.family.search);
    },
    clearFilters() {
      this.$set(this.request.birds, 'search', null);
      this.$set(this, 'form', { family: null, familyGroup: null });
    },
    async getBirds(event) {
      try {
        const { page, offset } = event;
        const { limit } = this.request.birds.pagination;
        const { familyGroup, family } = this.form;
        const { id: familyId } = family || {};
        const { id: familyGroupId } = familyGroup || {};
        let wordToSearch = createWordToSearch(this.request.birds.search);

        const variables = {
          offset,
          limit,
          wordToSearch,
          familyId,
          familyGroupId,
        };

        this.$set(this.request.birds, 'loading', true);

        await this.$apollo
          .query({
            fetchPolicy: 'network-only',
            nextFetchPolicy: 'cache-and-network',
            query: GET_ALL_BIRDS_STCALA_WITH_FILTERS_QUERY,
            variables,
          })
          .then(({ data }) => {
            const totalBirds = data.birds_aggregate.aggregate.count;
            const limit = this.request.birds.pagination.limit;
            const maxPages = calculateMaxPages(totalBirds, limit);

            this.$set(this.request.birds.pagination, 'maxPages', maxPages);

            const birds = this.ordenateBirds(data.birds);

            this.$set(this.request.birds, 'data', birds);
          })
          .catch((error) => {
            this.logError(error, 'LodgeBirdsSelect@getBirds:247');
          })
          .finally(() => {
            this.$set(this.request.birds.pagination, 'actualPage', page);
            this.$set(this.request.birds, 'loading', false);
          });
      } catch (error) {
        this.logError(error, 'LodgeBirdsSelect@getBirds:368');
      }
    },
    ordenateBirds(birds) {
      return birds.map((bird) => {
        const {
          family,
          id,
          name_en: commonName,
          scientific_name: scientific,
          photos,
        } = bird;

        const { family_group } = family;

        const { mid_img: img } = photos[0] || {};

        return {
          id,
          bird: {
            img: img,
            specie: {
              commonName,
              scientific,
            },
          },
          familyGroup: family_group.name_en,
          family: family.name_en,
        };
      });
    },
    async getFamilyGroup(query) {
      try {
        const { limit } = this.request.familyGroup;
        let wordToSearch = createWordToSearch({ value: query });

        const variables = {
          limit,
          wordToSearch,
        };

        this.$set(this.request.familyGroup, 'isLoading', true);

        await this.waitToSearch(1000).then(() => {
          this.$apollo
            .query({
              query: GET_ALL_FAMILY_GROUPS_QUERY,
              variables,
            })
            .then(({ data }) => {
              const family_groups = this.ordenateFamilyGroups(
                data.family_groups
              );

              this.$set(this.request.familyGroup, 'data', family_groups);
            })
            .catch((error) => {
              this.logError(error, 'LodgeBirdsSelect@getFamilyGroup:424');
            })
            .finally(() => {
              this.$set(this.request.familyGroup, 'isLoading', false);
              this.$set(this.request.familyGroup, 'search', query);
            });
        });
      } catch (error) {
        this.logError(error, 'LodgeBirdsSelect@getFamilyGroup:432');
      }
    },
    ordenateFamilyGroups(familyGroups) {
      return familyGroups.map((group) => {
        return {
          name: group.name_en,
          id: group.id,
        };
      });
    },
    async getFamily(query) {
      try {
        const { limit } = this.request.family;
        const { id: familyGroupId } = this.form.familyGroup || {};
        let wordToSearch = createWordToSearch({ value: query });

        const variables = {
          limit,
          wordToSearch,
          familyGroupId,
        };

        this.$set(this.request.family, 'isLoading', true);

        await this.waitToSearch(1000).then(() => {
          this.$apollo
            .query({
              query: GET_ALL_FAMILIES_QUERY,
              variables,
            })
            .then(({ data }) => {
              const families = this.ordenateFamilies(data.families);

              this.$set(this.request.family, 'data', families);
            })
            .catch((error) => {
              this.logError(error, 'LodgeBirdsSelect@getFamily:403');
            })
            .finally(() => {
              this.$set(this.request.family, 'isLoading', false);
              this.$set(this.request.family, 'search', query);
            });
        });
      } catch (error) {
        this.logError(error, 'LodgeBirdsSelect@getFamily:474');
      }
    },
    ordenateFamilies(families) {
      return families.map((family) => {
        const { family_group, id, name_en: name } = family;

        return {
          family_group,
          id,
          name,
        };
      });
    },
    handleNewSearch() {
      this.createParamsQuerys();
      this.getBirds({ page: 1, offset: 0 });
    },
    createParamsQuerys() {
      const familyObj = this.form.family || {};
      const familyGroupObj = this.form.familyGroup || {};
      const family = familyObj.id ? createQuery(familyObj) : undefined;
      const familyGroup = familyGroupObj.id
        ? createQuery(familyGroupObj)
        : undefined;
      const search = createQueryFromInput(this.request.birds.search);

      this.$router.replace({
        query: {
          ...this.$route.query,
          family,
          familyGroup,
          search,
        },
      });
    },
    closeModals() {
      this.$set(this.modal, 'edit', {
        isEditing: false,
        birdIndex: null,
        isLoading: false,
      });
      this.$set(this.modal, 'delete', {
        isDeleting: false,
        birdIndex: null,
        isLoading: false,
      });
    },
    editBird(id) {
      this.modal.edit.birdIndex = this.request.birds.data.findIndex(
        (item) => item.id == id
      );

      if (this.modal.edit.birdIndex !== -1) {
        this.modal.edit.isEditing = true;
      }
    },
    birdToEdit(index) {
      const bird = this.request.birds.data[index];

      if (bird) {
        const { id } = bird;
        const { specie, img } = bird.bird;

        return {
          id,
          specie,
          months: {},
          hotel_id: this.hotelId,
          img,
        };
      }
    },
    async saveEdit(params) {
      try {
        const { id: bird_id, hotel_id, months, notable } = params.bird;

        const {
          apr: APR,
          aug: AUG,
          dec: DEC,
          jan: JAN,
          feb: FEB,
          jul: JUL,
          jun: JUN,
          mar: MAR,
          may: MAY,
          nov: NOV,
          sep: SEP,
          oct: OCT,
        } = months;

        const variables = {
          bird_id,
          hotel_id,
          JAN,
          FEB,
          MAR,
          APR,
          MAY,
          JUN,
          JUL,
          AUG,
          SEP,
          OCT,
          NOV,
          DEC,
        };

        this.$set(this.modal.edit, 'isLoading', true);

        await this.$apollo
          .mutate({
            mutation: INSERT_BIRDS_BY_HOTEL_MUTATION,
            variables,
          })
          .then(async () => {
            this.request.birdsSelected.data.push(bird_id);

            await params
              .updateIsNotable(hotel_id, bird_id, notable)
              .then(() => {
                this.$emit('createNotify', {
                  message: 'savedSuccessfully',
                  color: 'success',
                });

                this.closeModals();
              })
              .catch((error) => {
                this.logError(error, 'LodgeBirdsSelect@saveEdit:625');
              });
          })
          .catch((error) => {
            this.logError(error, 'LodgeBirdsSelect@saveEdit:629');
          })
          .finally(() => {
            this.$set(this.modal.edit, 'isLoading', false);
          });
      } catch (error) {
        this.logError(error, 'LodgeBirdsSelect@saveEdit:635');
      }
    },
    async getBirdsSelecteds() {
      try {
        const variables = {
          hotelId: this.hotelId,
        };

        this.$set(this.request.birdsSelected, 'loading', true);

        this.$apollo
          .query({
            fetchPolicy: 'network-only',
            nextFetchPolicy: 'cache-and-network',
            query: GET_BIRDS_SELECTED_BY_HOTEL_QUERY,
            variables,
          })
          .then(({ data }) => {
            this.$set(
              this.request.birdsSelected,
              'data',
              data.hotels_birds.map((bird) => bird.bird)
            );
          })
          .catch((error) => {
            this.logError(error, 'LodgeBirdsSelect@getBirdsSelecteds:661');
          })
          .finally(() => {
            this.$set(this.request.birdsSelected, 'loading', false);
          });
      } catch (error) {
        this.logError(error, 'LodgeBirdsSelect@getBirdsSelecteds:667');
      }
    },
    handleDeleteBird(id) {
      const bird = this.request.birds.data.find((bird) => {
        return bird.id == id;
      });

      if (bird) {
        this.$set(this.modal.delete, 'isDeleting', true);
        this.$set(this.modal.delete, 'birdId', bird.id);
        this.$set(this.modal.delete, 'hotelId', this.hotelId);
      }
    },
    deleteBird() {
      try {
        const { birdId: id, hotelId } = this.modal.delete;

        const variables = { id, hotelId };

        this.$set(this.modal.delete, 'isLoading', true);

        this.$apollo
          .mutate({
            mutation: DELETE_BIRD_BY_HOTEL_MUTATION,
            variables,
          })
          .then(() => {
            const birdIndex = this.request.birdsSelected.data.findIndex(
              (birdId) => {
                return birdId == id;
              }
            );

            if (birdIndex !== -1) {
              this.request.birdsSelected.data.splice(birdIndex, 1);
            }

            this.$emit('createNotify', {
              message: 'savedSuccessfully',
              color: 'success',
            });

            this.closeModals();
          })
          .catch((error) => {
            this.logError(error, 'LodgeBirdsSelect@deleteBird:713');
          })
          .finally(() => {
            this.$set(this.modal.delete, 'isLoading', false);
          });
      } catch (error) {
        this.logError(error, 'LodgeBirdsSelect@deleteBird:719');
      }
    },
  },
  computed: {
    hotelId() {
      return this.isAdmin ? this.$route.params.id : this.user.hotel_id;
    },
  },
  async created() {
    this.tableColumns = TableLodgeBirdsSelect;
    this.defaultImage = defaultImage;

    try {
      const { page, family, familyGroup, search } = this.$route.query;

      this.$set(this.form, 'family', parseQuery(family));
      this.$set(this.form, 'familyGroup', parseQuery(familyGroup));
      this.$set(this.request.birds, 'search', parseInputQuery(search));
      this.$set(
        this.request.birds.pagination,
        'actualPage',
        parsePageQuery(page)
      );
      this.$set(
        this.request.birds.pagination,
        'maxPages',
        parsePageQuery(page)
      );

      await this.getBirdsSelecteds();
    } catch (error) {
      this.logError(error, 'LodgeBirdsSelect@created:751');
    }
  },
};
</script>

<style>
.LodgeBirdsSelect_filters__multiselect_container {
  @apply grid gap-3 grid-cols-2;
}

.LodgeBirsSelect_filters__input {
  @apply w-full sm:w-1/2;
}

.LodgeBirdsSelect_table__bird {
  @apply grid gap-2;
}

@media screen AND (min-width: 640px) {
  .LodgeBirdsSelect_table__bird {
    grid-template-columns: 44px 1fr;
    grid-template-rows: 44px;
  }
}

.LodgeBirdsSelect_filters__container {
  @apply flex flex-col justify-between mb-2 sm:grid sm:grid-cols-2;
}
</style>
